import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { PracticeAnalyticsTypes } from '@frontend/api-analytics';
import {
  AnalyticsHooks,
  DemoDataBanner,
  GeneralFeedbackButton,
  MultiLocationLargeModeOnlyFeature,
} from '@frontend/analytics-shared';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { shell } from '@frontend/shell-utils';
import { useNavigate, useSearch, type AnyRoute } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { BreadcrumbPathProps, ContentLoader } from '@frontend/design-system';
import { Banner } from '../components/banner';
import { useIsPADemoAccount } from '../components/practice/hooks/use-is-pa-demo-account';
import { usePracticeAnalyticsShallowStore } from '../components/practice/hooks/use-practice-analytics-store';
import { ActivePatientsSubView } from '../components/practice/sub-views/active-patients-sub-view';
import { ActivePatientsUnscheduledSubView } from '../components/practice/sub-views/active-patients-unscheduled-sub-view';
import { CancellationsSubView } from '../components/practice/sub-views/cancellations-sub-view';
import { HygieneFollowUpSubView } from '../components/practice/sub-views/hygiene-follow-up-sub-view';
import { HygieneTreatmentSubView } from '../components/practice/sub-views/hygiene-treatment-sub-view';
import { NewPatientsSubView } from '../components/practice/sub-views/new-patients-sub-view';
import { PractitionerAnalysisSubView } from '../components/practice/sub-views/practitioner-analysis-sub-view';
import { RecapturedPatientsSubView } from '../components/practice/sub-views/recaptured-patients-sub-view';
import { RestorativeTreatmentSubView } from '../components/practice/sub-views/restorative-treatment-sub-view';
import { URLs } from '../constants';
import { trackingIds } from '../tracking-ids';

type PageId =
  | 'cancelled-appointments'
  | 'demographics'
  | 'hygiene-follow-up'
  | 'hygiene-treatment'
  | 'new-patients'
  | 'practitioner-analysis'
  | 'recaptured-patients'
  | 'restorative-treatment'
  | 'unscheduled-patients';

interface Props {
  id: PageId;
}

export const PracticeAnalyticsSubView: FC<React.PropsWithChildren<Props>> = ({ id }) => {
  const { t } = useTranslation('analytics');
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedLocationIds } = useAppScopeStore();
  const { isSmallMode } = useIsSmallMode();
  const { filterHintText, filters, isDemoModal, isPageReady, setIsDemoAccount, setIsPageReady, showDemoChipAndBanner } =
    usePracticeAnalyticsShallowStore(
      'filterHintText',
      'filters',
      'isDemoModal',
      'isPageReady',
      'setIsDemoAccount',
      'setIsPageReady',
      'showDemoChipAndBanner'
    );
  const isDemoAccount = useIsPADemoAccount();
  const { locationIds, locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isSuperAdmin = hasSchemaACL(selectedLocationIds[0], Permission.ANALYTICS_READ);
  const [multiLevelBreadcrumbData, setMultiLevelBreadcrumbData] = useState<BreadcrumbPathProps>({ label: '', to: '' });

  const subView = useMemo(() => {
    if (!isPageReady) {
      return null;
    }

    // Reset
    if (multiLevelBreadcrumbData.label) {
      setMultiLevelBreadcrumbData({
        label: '',
        to: '',
      });
    }

    const commonSubViewProps: PracticeAnalyticsTypes.MetricSubViewProps = {
      exportEnabled: isSuperAdmin,
      isDemoAccount,
    };

    switch (id) {
      case 'cancelled-appointments':
        return {
          title: t('Cancellations'),
          view: <CancellationsSubView {...commonSubViewProps} />,
        };

      case 'demographics':
        return {
          view: <ActivePatientsSubView {...commonSubViewProps} />,
          title: t('Active Patients'),
        };

      case 'hygiene-follow-up':
        return {
          view: <HygieneFollowUpSubView {...commonSubViewProps} />,
          title: t('Hygiene Follow-Up'),
        };

      case 'hygiene-treatment':
        return {
          view: <HygieneTreatmentSubView {...commonSubViewProps} />,
          title: t('Hygiene Treatment Plan'),
        };

      case 'new-patients':
        return {
          view: <NewPatientsSubView {...commonSubViewProps} />,
          title: t('New Patients'),
        };

      case 'practitioner-analysis': {
        const banner = isSmallMode ? (
          <Banner
            buttonLabel={t('Switch to Large Mode')}
            description={t(
              'Get complete comparison and performance insights across all providers. Switch to Large Mode to access the full breakdown.'
            )}
            iconName='info-badge'
            onButtonClick={() => shell?.invoke?.('main:update-options', { width: 1500 })}
            styleConfig={{
              backgroundColor: theme.colors.primary5,
              iconColor: theme.colors.primary50,
            }}
            title={t('Unlock Full Practitioner Analysis')}
            trackingIdBase='practice-analytics-practitioner-analysis-banner'
          />
        ) : null;

        if (search.locationId) {
          setMultiLevelBreadcrumbData({
            label: t('Practitioner Analysis'),
            to: `${URLs.PRACTICE_ANALYTICS_BASE}/practitioner-analysis`,
          });

          return {
            title: locationNames[search.locationId] || search.locationId,
            view: <PractitionerAnalysisSubView {...commonSubViewProps} locationId={search.locationId} />,
            banner,
          };
        } else {
          return {
            view: <PractitionerAnalysisSubView {...commonSubViewProps} />,
            title: t('Practitioner Analysis'),
            banner,
          };
        }
      }

      case 'recaptured-patients':
        return {
          view: <RecapturedPatientsSubView {...commonSubViewProps} />,
          title: t('Recaptured'),
        };

      case 'restorative-treatment':
        return {
          view: <RestorativeTreatmentSubView {...commonSubViewProps} />,
          title: t('Restorative Treatment Plan'),
        };

      case 'unscheduled-patients':
        return {
          view: <ActivePatientsUnscheduledSubView {...commonSubViewProps} />,
          title: t('Unscheduled Patients'),
        };

      default:
        return null;
    }
  }, [
    isPageReady,
    isSmallMode,
    multiLevelBreadcrumbData.label,
    isSuperAdmin,
    isDemoAccount,
    id,
    t,
    search.locationId,
    locationNames,
  ]);

  const breadcrumbs: BreadcrumbPathProps[] = useMemo(() => {
    return [
      {
        label: isDemoModal ? '' : t('Analytics'),
        to: '/analytics/dashboard',
      },
      {
        label: t('Practice Analytics'),
        to: URLs.PRACTICE_ANALYTICS_MAIN,
      },
      multiLevelBreadcrumbData,
      {
        label: subView?.title || '',
      },
    ].filter(({ label }) => !!label);
  }, [isDemoModal, multiLevelBreadcrumbData, subView?.title, t]);

  useEffect(() => {
    setIsDemoAccount(isDemoAccount);
  }, [isDemoAccount, setIsDemoAccount]);

  useEffect(() => {
    // Go back to the main page when filters not available
    if (!Object.keys(filters).length) {
      navigate({ to: '/analytics/practice/main' });
    } else {
      setIsPageReady(true);
    }
  }, [search.locationId]);

  return (
    <>
      {showDemoChipAndBanner && (
        <DemoDataBanner
          css={styles.demoBanner}
          onCTAClick={() => navigate({ to: URLs.PRACTICE_ANALYTICS_SUBSCRIBE as AnyRoute })}
          startTrialTrackingId={trackingIds.practiceAnalyticsPLG.plgFreeTrialFromDemoSubPage}
          subtitle={t("As a subscriber, you'll see your own practice's data updated daily.")}
          title={t("You're taking Practice Analytics for a test drive with demo data!")}
        />
      )}
      {subView?.banner}
      <Page>
        <Page.Header>
          <Page.Header.Breadcrumbs breadcrumbs={breadcrumbs} />
          <Page.Header.Heading css={styles.heading}>
            <Page.Header.Title title={subView?.title} className='header-title' />
            {id !== 'demographics' && id !== 'unscheduled-patients' && (
              <Page.Header.Subtitle
                subtitle={t('Showing results for {{filtersInfo}}', { filtersInfo: filterHintText })}
              />
            )}
            <GeneralFeedbackButton selectedLocationFromPageFilter={filters.locations} />
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body>
          <div style={{ width: '100%' }} ref={containerRef}>
            {subView?.view}
          </div>
          <ContentLoader show={!isPageReady} />
          <MultiLocationLargeModeOnlyFeature locationCount={locationIds?.length} />
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  demoBanner: css`
    isolation: isolate;
    margin-bottom: 0;
    position: sticky;
    top: 0;
    z-index: 4;
  `,
  heading: css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    @container (max-width:  ${breakpoints.small.max}px) {
      flex-direction: row;
      margin-bottom: ${theme.spacing(1)};
    }
  `,
};
